import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AssetSelectorComponent } from '../../shared/design-system/asset-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CatalogService, MaintenancePlan } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-cmms-maintenance-plans',
  imports: [
    AinPageComponent, AinStateComponent, FormsModule, TableModule, ButtonModule,
    InputTextModule, InputNumberModule, AssetSelectorComponent
  ],
  template: `
    <ain-page [title]="t('catalog.cmms.plans.title')" [description]="t('catalog.cmms.plans.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('catalog.action.create')" (click)="showCreate.set(!showCreate())"></button>
            <button pButton type="button" [label]="t('catalog.action.refresh')" severity="secondary" (click)="load()"></button>
          </div>
          @if (showCreate()) {
            <div class="form-grid">
              <label><span>{{ t('catalog.cmms.planCode') }}</span><input pInputText [(ngModel)]="form.planCode" /></label>
              <label><span>{{ t('catalog.service.title') }}</span><input pInputText [(ngModel)]="form.name" /></label>
              <label><span>{{ t('catalog.assets.asset') }}</span><ain-asset-selector [(ngModel)]="form.assetId" /></label>
              <label><span>{{ t('catalog.cmms.intervalDays') }}</span><p-inputNumber [(ngModel)]="form.intervalDays" [min]="1" /></label>
              <button pButton type="button" [label]="t('catalog.action.save')" (click)="save()"></button>
            </div>
          }
          <div class="filter-toggle">
            <button pButton type="button" [label]="showFilters() ? t('catalog.filter.hide') : t('catalog.filter.show')" severity="secondary" (click)="showFilters.set(!showFilters())"></button>
          </div>
          @if (showFilters()) {
            <div class="filters">
              <label><span>{{ t('catalog.assets.asset') }}</span><ain-asset-selector [(ngModel)]="assetFilter" (ngModelChange)="load()" /></label>
            </div>
          }
          <p-table [value]="rows()" [paginator]="true" [rows]="15">
            <ng-template pTemplate="header">
              <tr>
                <th style="width: 3rem"><input type="checkbox" aria-label="select all" /></th>
                <th>{{ t('catalog.cmms.planCode') }}</th>
                <th>{{ t('catalog.service.title') }}</th>
                <th>{{ t('catalog.cmms.triggerType') }}</th>
                <th>{{ t('catalog.cmms.nextDueDate') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><input type="checkbox" [attr.aria-label]="row.planCode" /></td>
                <td>{{ row.planCode }}</td>
                <td>{{ row.name }}</td>
                <td>{{ row.triggerType }}</td>
                <td>{{ row.nextDueDate || '-' }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; }
    .form-grid, .filters { display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); padding: 12px; border: 1px solid #e2e8f0; border-radius: 8px; }
    .form-grid label, .filters label { display: grid; gap: 6px; }
  `]
})
export class CmmsMaintenancePlansComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly rows = signal<MaintenancePlan[]>([]);
  readonly showCreate = signal(false);
  readonly showFilters = signal(false);
  assetFilter: string | null = null;

  form = { planCode: '', name: '', assetId: null as string | null, intervalDays: 30 };

  ngOnInit(): void { this.load(); }

  load(): void {
    this.loading.set(true);
    this.service.listMaintenancePlans(this.assetFilter ?? undefined).subscribe({
      next: rows => { this.rows.set(rows); this.loading.set(false); },
      error: () => { this.rows.set([]); this.loading.set(false); }
    });
  }

  save(): void {
    if (!this.form.planCode.trim() || !this.form.assetId) return;
    this.service.createMaintenancePlan({
      assetId: this.form.assetId,
      planCode: this.form.planCode,
      name: this.form.name || this.form.planCode,
      triggerType: 'Time',
      intervalDays: this.form.intervalDays,
      meterInterval: 0,
      eventCode: '',
      nextDueDate: null,
      nextDueMeter: null
    }).subscribe({ next: () => { this.showCreate.set(false); this.load(); } });
  }

  t(key: string): string { return this.localization.translate(key); }
}
